import React from 'react';
import { FieldState } from 'formstate';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { MatField } from 'src/core/components/mat-field';
import { Select, Button, MenuItem, FormControl, InputLabel } from '@material-ui/core';
import * as Styles from './loan-information.less';
import * as Utils from '../../forms/application-information-forms-utils';
import { LoanForm } from '../../forms/application-information-forms';
import { CalculatedProgram } from 'src/api';

interface ILoanInformationProps {
    loan: LoanForm;
    state: FieldState<string>;
    states: string[];
    showAllPlans: (programName: string, totalPrice: number, stateCode: string) => Promise<void>;
    products: string[];
    validate: (fieldState: FieldState<string>) => () => Promise<void>;
    isLoanAmountValid: boolean;
    plans: CalculatedProgram[];
    isLoadingPlans: boolean;
}

export const LoanInformation: React.FC<ILoanInformationProps> = observer(
    ({
        loan,
        state,
        showAllPlans,
        states,
        products,
        validate,
        isLoanAmountValid,
        plans,
        isLoadingPlans,
    }) => {
        return (
            <>
                <div className={Styles.row}>
                    <div className={Styles.col}>
                        <MatField
                            required
                            fullWidth
                            errorColor
                            fieldState={loan.$.SalesAmount}
                            maxLength={26}
                            formatter={Utils.formatAmount}
                            validator={Utils.amountValidator}
                            label="Sale Amount"
                            startAdornment="$"
                            onBlur={validate(loan.$.SalesAmount)}
                        />
                    </div>
                    <div className={Styles.col}>
                        <MatField
                            fullWidth
                            errorColor
                            fieldState={loan.$.DownPayment}
                            maxLength={26}
                            formatter={Utils.formatAmount}
                            validator={Utils.amountValidator}
                            label="Down Payment"
                            startAdornment="$"
                            onBlur={validate(loan.$.DownPayment)}
                        />
                    </div>
                </div>
                <div className={Styles.row}>
                    <div className={Styles.col}>
                        <FormControl required fullWidth>
                            <InputLabel htmlFor="select">Improvement Type</InputLabel>
                            <Select
                                inputProps={{ id: 'improvement' }}
                                value={loan.$.Product.value}
                                onChange={async (e) => {
                                    loan.$.Product.onChange(e.target.value as string);
                                    await validate(loan.$.Product)();
                                }}
                            >
                                {products.map((value, index) => (
                                    <MenuItem key={index} value={value}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <div className={Styles.col}>
                        <FormControl required fullWidth>
                            <InputLabel htmlFor="select">Job State</InputLabel>
                            <Select
                                inputProps={{ id: 'jobState' }}
                                value={state.value}
                                onChange={async (e) => {
                                    state.onChange(e.target.value as string);
                                    await validate(state)();
                                }}
                            >
                                {states.map((value, index) => (
                                    <MenuItem key={index} value={value}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                </div>

                <Button
                    className={Styles.showPlansButton}
                    disabled={
                        !loan.$.SalesAmount.value ||
                        !loan.$.Product.value ||
                        !state.value ||
                        isLoadingPlans
                    }
                    onClick={() =>
                        showAllPlans(
                            loan.$.Product.value,
                            parseFloat(loan.$.SalesAmount.value) -
                                parseFloat(loan.$.DownPayment.value || '0'),
                            state.value
                        )
                    }
                    color="primary"
                >
                    {isLoadingPlans ? 'Loading ...' : 'Show All Plans'}
                </Button>

                {loan.$.SalesAmount.value && plans.length > 0 && (
                    <div
                        className={classnames(
                            Styles.fadeIn,
                            Styles.desiredAmount,
                            !isLoanAmountValid && Styles.error
                        )}
                    >
                        <div className={Styles.subTitle}>Desired Loan Amount</div>
                        <div className={Styles.title}>
                            $
                            {(
                                parseFloat(loan.$.SalesAmount.value) -
                                parseFloat(loan.$.DownPayment.value || '0')
                            ).toFixed(2)}
                        </div>
                    </div>
                )}
            </>
        );
    }
);
